import { Button, Text } from "@chakra-ui/react";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";

import { AppDispatch } from "../App";
import NumberInput from "../components/NumberInput";
import { useErrorIfModelNotUpToDate } from "../hooks/queries";
import { checkPermission } from "../state/appState";
import { removeSelected, setSelected } from "../state/existingRasterEditsMenu";
// functions used from layerRasterSourcesMenufrom: retrieveConfiguredRasters, getList, getStatus
// imported * to avoid import conflicts
import {
  NewValue,
  PolygonStatus,
  getApiRequestStatus,
  getIsReadyToFinalizeConfirm,
  getIsUndoLastPointRelevant,
  getNewValue,
  getStatus,
  removeLastPointAction,
  setNewValueAction,
  setStatusAction,
  submitLeveeEdit,
} from "../state/leveeEdit";
import { Level, remindUserToPressPlay, sendMessage } from "../state/mainGui";
import { getThreediModel } from "../state/simulation";
import ErrorMessageStyles from "../styles/ErrorMessage.module.css";
import FormStyles from "../styles/Forms.module.css";
import showHideStyles from "../styles/ShowHide.module.css";
import { buttonRibbonStyle } from "../styles/styleComposition";
import { formattedMessageForErrorCode } from "../utils/authorizationUtils";

import * as layerRasterSourcesMenufrom from "./../state/layerRasterSourcesMenu";
import CollapsableSidebarStyles from "./CollapsableSidebar.module.css";
import ExistingLeveeEdits from "./existing/ExistingLeveeEdits";
import ExistingItemsContainer from "./ExistingItemsContainer";
import { FormLabel } from "./FormLabel";
import LeftMenuHeader from "./LeftMenuHeader";
import menuStyles from "./Menu.module.css";

type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;

const LeveeEditMenu: React.FC<PropsFromDispatch> = (props) => {
  const intl = useIntl();
  const editApiCallState = useSelector(getApiRequestStatus);
  const threediModel = useSelector(getThreediModel);

  const isReadyToFinalizeConfirm = useSelector(getIsReadyToFinalizeConfirm);
  const newValue = useSelector(getNewValue);
  const rasterEditStatus = useSelector(getStatus);
  const isUndoLastPointRelevant = useSelector(getIsUndoLastPointRelevant);
  const sourceRasters = useSelector(layerRasterSourcesMenufrom.getList);
  const sourceRasterGetStatus = useSelector(layerRasterSourcesMenufrom.getStatus);

  const errorMessage = useErrorIfModelNotUpToDate("Failed to add levee.", threediModel);

  const demRaster = sourceRasters.find((raster) => raster.type === "dem_file");
  const interceptionRaster = sourceRasters.find((raster) => raster.type === "interception_file");
  const disabledBecauseRastersNotPresent =
    !demRaster && !interceptionRaster && sourceRasterGetStatus === "SUCCESS";

  const permission = useSelector(checkPermission)("editRaster");
  const disableWholeForm =
    disabledBecauseRastersNotPresent || permission.allowed === false || editApiCallState === "SENT";

  const disablesWholeFormErrorMessage =
    (disabledBecauseRastersNotPresent && (
      <FormattedMessage
        id="rasterEditMenu.need_interception_or_dem_raster"
        defaultMessage="Raster edit not available for models without 2D"
      />
    )) ||
    (permission.allowed === false && formattedMessageForErrorCode(permission.errorCode, intl));

  return (
    <div className={`${CollapsableSidebarStyles.LeftMenu}`}>
      <LeftMenuHeader>
        <FormattedMessage id="editLevee.header" defaultMessage="Create flood barrier" />
      </LeftMenuHeader>
      <div
        className={`${menuStyles.SubMenu} ${menuStyles.List}`}
        style={{ padding: "0 8px 0 8px" }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className={FormStyles.NormalForm}
        >
          <fieldset disabled={disableWholeForm}>
            <FormLabel>
              <FormattedMessage id="editRasterMenu.elevation" defaultMessage="Elevation" />
            </FormLabel>
            <NumberInput
              value={newValue}
              unit={<FormattedMessage id="editRasterMenu.unit_mmsl" defaultMessage="mMSL" />}
              setValue={props.setNewValueAction}
              disabled={disabledBecauseRastersNotPresent}
              allowDecimals
              allowNegative
              required
            />
            <Text fontSize="12px" color="brand.clear_creek" margin="0" fontStyle="italic">
              Flood barrier only affects the flow where it intersects flowlines
            </Text>
            <div
              className={`${
                rasterEditStatus === "SELECT_OR_NEW"
                  ? showHideStyles.ShowAnimate
                  : showHideStyles.NoShowAnimate
              }`}
            >
              <div className={buttonRibbonStyle}>
                <Button
                  variant="primary"
                  onClick={() => {
                    props.setStatusAction("DRAWING_MODE");
                    props.removeSelected();
                  }}
                >
                  <FormattedMessage
                    id="editRasterMenu.place_on_map_button"
                    defaultMessage="DRAW ON MAP"
                  />
                </Button>
              </div>
            </div>
            <div
              className={`${
                rasterEditStatus === "DRAWING_MODE" && permission.allowed === true
                  ? showHideStyles.ShowAnimate
                  : showHideStyles.NoShowAnimate
              }`}
            >
              <div className={menuStyles.ListItem}>
                <span>
                  <FormattedMessage
                    id="dischargeMenu.drawing_mode_place_cursor_on_map_to_draw"
                    defaultMessage="Place your cursor on the map to draw the item"
                  />
                </span>
              </div>
            </div>
            <div
              className={`${
                rasterEditStatus === "DRAWING_MODE"
                  ? showHideStyles.ShowAnimate
                  : showHideStyles.NoShowAnimate
              }`}
            >
              <div className={buttonRibbonStyle}>
                <Button
                  variant="primary"
                  onClick={(e) => {
                    const target = e.target as HTMLButtonElement;
                    if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                      props.submitLeveeEdit(errorMessage);
                    }
                  }}
                  isDisabled={!isReadyToFinalizeConfirm}
                >
                  <FormattedMessage
                    id="editRasterMenu.button_confirm drawing"
                    defaultMessage="Confirm"
                  />
                </Button>
              </div>
              <div className={buttonRibbonStyle}>
                <Button
                  variant="secondary"
                  onClick={props.removeLastPointAction}
                  isDisabled={!isUndoLastPointRelevant}
                >
                  <FormattedMessage
                    id="editLeveeMenu.button_undolastpoint"
                    defaultMessage="Undo last point"
                  />
                </Button>
              </div>
              <div className={buttonRibbonStyle}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    props.setStatusAction("SELECT_OR_NEW");
                  }}
                >
                  <FormattedMessage
                    id="editRasterMenu.button_cancel_drawing_mode"
                    defaultMessage="Cancel"
                  />
                </Button>
              </div>
            </div>

            <div className={`${menuStyles.ListItem} `}>
              {disablesWholeFormErrorMessage ? (
                <span className={ErrorMessageStyles.ErrorMessageStyle}>
                  {disablesWholeFormErrorMessage}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
      <ExistingItemsContainer>
        <ExistingLeveeEdits />
      </ExistingItemsContainer>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setNewValueAction: (newValue: NewValue) => dispatch(setNewValueAction(newValue)),
    setStatusAction: (status: PolygonStatus) => dispatch(setStatusAction(status)),
    submitLeveeEdit: (errorMessage: string | JSX.Element) =>
      dispatch(submitLeveeEdit(errorMessage)),
    sendMessage: (message: string | JSX.Element, timeoutMs: number, level?: Level) =>
      dispatch(sendMessage(message, timeoutMs, level)),
    setSelected: (selectedUid: string | null) => dispatch(setSelected(selectedUid)),
    removeSelected: () => dispatch(removeSelected()),
    remindUserToPressPlay: () => dispatch(remindUserToPressPlay()),
    removeLastPointAction: () => dispatch(removeLastPointAction()),
  };
};

export default connect(null, mapDispatchToProps)(LeveeEditMenu);
